import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { SortOrder } from '../../../api/shared.dto';
import { IZoomDAO, IZoomTelephoneNumberSetDTO, TTelephoneNumberSetsRequestSortKeys } from '../../../api/zoom.dao';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { IRouter } from '../../../infrastructure/router';
import { RouteParams } from '../../../routes-table';
import { PagingViewModel } from '../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { Unsubscribe } from 'router5/dist/types/base';
import { TelephoneNumberSetForm } from '../_telephone-number-set-groups/telephone-form';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../infrastructure/sub-windows';

class TelephoneNumberSetsViewModel {
    @mobx.observable
    public pagingVM: PagingViewModel;
    @mobx.observable
    private telephoneNumberSets: IZoomTelephoneNumberSetDTO[];
    @mobx.observable
    private isReadyForRefetch: boolean;
    @mobx.observable
    private fetchError: string;
    @mobx.observable
    private searchQuery: string;
    @mobx.observable
    private telephoneNameToEditPopupMap: { [key: string]: ISubWindowFacade } = {};

    private subscriptions: Unsubscribe[];

    private sortOrder: SortOrder;
    private sortField: TTelephoneNumberSetsRequestSortKeys;

    constructor(
        @IoC.Inject private readonly zoomDao: IZoomDAO,
        @IoC.Inject private readonly router: IRouter<RouteParams>,
        @IoC.Inject private readonly errorParsing: IErrorParsing,
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) {
        this.pagingVM = new PagingViewModel();
        mobx.reaction(
            () => this.pagingVM.getCurrentPage(),
            this.fetchTelephoneNumberSets
        );
        this.cleanup();

        this.isReadyForRefetch = false;
        this.sortOrder = SortOrder.Asc;
        this.sortField = 'name';
        this.searchQuery = '';
    }

    public getTelephoneNumberSets = (): IZoomTelephoneNumberSetDTO[] => {
        return this.telephoneNumberSets;
    };

    public getPagingVM = (): PagingViewModel => {
        return this.pagingVM;
    };

    public getSortOrder = (): SortOrder => {
        return this.sortOrder;
    };

    public getFetchError = (): string => {
        return this.fetchError;
    };

    public getSearchQuery = (): string => {
        return this.searchQuery;
    };

    public getSortField = (): TTelephoneNumberSetsRequestSortKeys => {
        return this.sortField;
    };

    public resetSearchAndFilter = (): void => {
        this.sortOrder = SortOrder.Asc;
        this.sortField = 'name';
        this.searchQuery = '';
    };

    @mobx.action
    public cleanup(): void {
        this.resetSearchAndFilter();
        this.telephoneNumberSets = null;
    }

    @mobx.action
    public executeSearch = (): void => {
        const currentPage = this.pagingVM.getCurrentPage();
        if (currentPage === 1) {
            this.fetchTelephoneNumberSets();
        } else {
            this.pagingVM.selectPage(1);
        }
    };

    @mobx.action
    public setSearchQuery = (query: string, needExecuteSearch = false): void => {
        const newSearchQuery = query.trim();

        if (this.searchQuery !== newSearchQuery) {
            this.searchQuery = newSearchQuery;

            if (needExecuteSearch) this.executeSearch();
        }
    };

    @mobx.action
    public handleErrorMessage = (value: string): void => {
        this.fetchError = value;
    };

    @mobx.action
    public handleChangeSearchQuery = (value: string): void => {
        this.searchQuery = value;
    };

    @mobx.action
    public isNeedRefetch = (): boolean => {
        return !this.searchQuery && this.isReadyForRefetch;
    };

    @mobx.action
    public sortTableRows = async (
        sortField: TTelephoneNumberSetsRequestSortKeys,
        sortOrder: SortOrder
    ): Promise<void> => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        await this.fetchTelephoneNumberSets();
    };

    @mobx.action
    public reFetchMeetings = (): void => {
        this.isReadyForRefetch = false;
        this.fetchTelephoneNumberSets();
    };

    @mobx.action
    public fetchTelephoneNumberSets = async (): Promise<void> => {
        try {
            const telephoneNumberSetsResponse =
                await this.zoomDao.getTelephoneNumberSets({
                    size: this.pagingVM.getPageSize(),
                    page: this.pagingVM.getCurrentPageForBE(),
                    query: this.searchQuery || '',
                    sortField: this.sortField,
                    sortOrder: this.sortOrder,
                });
            mobx.runInAction(() => {
                this.telephoneNumberSets = telephoneNumberSetsResponse.content;
                this.pagingVM.setTotalRecords(
                    telephoneNumberSetsResponse.totalElements
                );
            });
        } catch (error) {
            const err = this.errorParsing.extractMessage(error);
            this.handleErrorMessage(err);
        }
    };

    @mobx.action
    public showTelephoneNumberSetForm = (title: string, audioProviderId?: string): ISubWindowFacade => {
        return this.subWindowService.showSubWindow({
            componentToHost: TelephoneNumberSetForm,
            componentToHostProps: {
                fetchTelephoneNumberSets: this.fetchTelephoneNumberSets,
                closeTelephoneNumberSetForm: this.closeTelephoneNumberSetForm,
                numberSet: this.telephoneNumberSets.find(
                    el => el.id.toString() === audioProviderId?.toString()
                ),
            },
            title: title,
        });
    };

    public closeTelephoneNumberSetForm = (id: string) => {
       this.telephoneNameToEditPopupMap[id].close();
    };

    public editNumberSet = (title: string, audioProviderId: string): void => {
        const alreadyShownSubWindow = this.telephoneNameToEditPopupMap[audioProviderId];

        if (
            alreadyShownSubWindow &&
            alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed
        ) {
            alreadyShownSubWindow.maximize();
        } else {
            this.telephoneNameToEditPopupMap[audioProviderId] = this.showTelephoneNumberSetForm(title, audioProviderId);
        }
    };

    public isNumberSetEditFormShown = (audioProviderId: string): boolean => {
        return (
            this.telephoneNameToEditPopupMap[audioProviderId] &&
            this.telephoneNameToEditPopupMap[audioProviderId].getStatus() !== SubWindowStatus.Closed
        );
    };
}

export { TelephoneNumberSetsViewModel };
